'use client';

import classNames from 'classnames';
import { Button } from '@get-mosh/msh-shared';
import List, { ListItemType } from 'src/components/List/List';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import getContentGroups from 'src/helpers/getContentGroups';
import {
    IconDefinition,
    faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { Content, RichTextField } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';

type HeroBannerProps = SliceComponentProps<Content.HeroBannerSlice> & {
    context: { pageType: string };
};

const HeroBanner = ({ slice, context }: HeroBannerProps) => {
    const HERO_BANNER_ON_RIGHT = slice.variation === 'heroImageOnTheRight';
    const DEFAULT = slice.variation === 'default';
    return (
        <section
            className={classNames('flex', {
                [`bg-${slice.primary.background}`]: slice.primary.background,
            })}
        >
            <div
                className={classNames(
                    'container flex w-full flex-col items-center gap-8 px-4 py-8 text-center lg:py-16',
                    {
                        'text-start md:flex-row md:gap-12':
                            HERO_BANNER_ON_RIGHT,
                    },
                )}
                id="container"
            >
                <div
                    className={classNames(
                        'flex flex-1 flex-col gap-6 lg:gap-8',
                        HERO_BANNER_ON_RIGHT ? 'items-start' : 'items-center',
                    )}
                    id="heroSection1"
                >
                    <div
                        className="flex flex-col gap-4 px-4 lg:gap-6"
                        id="text-container"
                    >
                        <div className="flex flex-col gap-2" id="title">
                            <h4 className="text-green-faint-dark">
                                {slice.primary.description}
                            </h4>
                            <h1
                                className={classNames(
                                    'hidden flex-col md:flex',
                                    DEFAULT
                                        ? 'items-center text-center'
                                        : 'items-start text-start',
                                    {
                                        'text-[48px]':
                                            slice.primary.make_heading_h2 ===
                                            true,
                                    },
                                )}
                            >
                                {slice.primary.title}
                            </h1>
                            <h1
                                className={classNames(
                                    'text-center md:hidden',
                                    DEFAULT
                                        ? 'items-center text-center'
                                        : 'items-start text-start',
                                    {
                                        'text-[32px]':
                                            slice.primary.make_heading_h2 ===
                                            true,
                                    },
                                )}
                            >
                                {slice.primary.mobile_title ||
                                    slice.primary.title}
                            </h1>
                        </div>
                        <div
                            className={classNames('flex flex-col gap-2', {
                                'items-center': slice.variation === 'default',
                            })}
                            id="hero-description"
                        >
                            <div className="hidden md:flex md:flex-col">
                                <HeroContent
                                    content={slice.primary.content}
                                    listIcon={faCircleCheck}
                                    className="flex max-w-[736px] flex-col gap-2"
                                    textStyles="text-[18px] leading-normal md:leading-normal md:text-[24px]"
                                />
                            </div>
                            <div className="flex flex-col gap-2 md:hidden">
                                <HeroContent
                                    content={
                                        slice.primary.mobile_content.length > 0
                                            ? slice.primary.mobile_content
                                            : slice.primary.content
                                    }
                                    className="mb-0 max-w-[736px]"
                                    textStyles="text-[18px] leading-normal"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="w-screen px-4 md:w-full" id="cta">
                        {slice.primary?.cta_label && (
                            <Button
                                className={classNames(
                                    'flex flex-1 justify-center',
                                    {
                                        'mx-auto': DEFAULT,
                                    },
                                )}
                                text={slice.primary.cta_label}
                                as="a"
                                href={slice.primary?.cta_link || '#'}
                                onClick={() => {
                                    trackClick(EventNames.BUTTON_CLICKED, {
                                        pageType: context.pageType,
                                        label: slice.primary.cta_label || '',
                                        link: slice.primary.cta_link || '',
                                        variation: slice.variation,
                                        location: slice.slice_type,
                                    });
                                }}
                                stretch
                            />
                        )}
                        <div className="flex flex-col pt-2 text-grey-400">
                            <PrismicRichText field={slice.primary.legal_text} />
                        </div>
                    </div>
                </div>
                {slice.primary.image?.url ? (
                    <PrismicNextImage
                        className={classNames(
                            'flex flex-1 flex-col items-start rounded-2xl shadow-[0px_0px_0px_4px_#003D3B,-4px_8px_0px_0px_#003D3B] md:w-[50%] md:max-w-[736px] md:shadow-[0px_0px_0px_4px_#003D3B,-16px_16px_0px_0px_#003D3B]',
                            `object-${slice.primary.image_fit}`,
                        )}
                        field={slice.primary.image}
                    />
                ) : null}
            </div>
        </section>
    );
};

const HeroContent = ({
    content,
    className,
    listIcon,
    textStyles,
}: {
    content: RichTextField;
    className?: string;
    listIcon?: IconDefinition;
    textStyles?: string;
}) => {
    const contentGroups = getContentGroups(content);
    return (
        <>
            {contentGroups.map((e, i) =>
                // This logic right here is very simple, we only allow for 2 types of content, either a paragraph or a list
                e.every((e) => e.type === 'list-item') ? (
                    <List
                        items={e as ListItemType[]}
                        key={i}
                        icon={{
                            faIcon: listIcon || faCircleCheck,
                            background: 'bg-none',
                            colour: 'text-green-dark',
                            border: 'border-none',
                        }}
                        className={className || ''}
                        textStyles={textStyles}
                    />
                ) : (
                    <p key={i} className={classNames(className, textStyles)}>
                        {(e[0] as { text: string }).text}
                    </p>
                ),
            )}
        </>
    );
};

export default HeroBanner;
